import { type UseQueryResult, useQuery } from '@tanstack/react-query'

import { getFeatureFlag, getFeatureFlagBoolean, getFeatureFlagKeys, getFeatureFlagString, getFeatureFlagValues } from './apis'
import { FEATURE_FLAG_STALE_TIME } from './consts'

/**
 * munoconf(AWS AppConfig) feature flag 쿼리 훅
 *
 * 새 flag 추가 방법:
 * 1. MunoConf Web UI(https://munoconf.ocmp.dev.musinsa.io)에서 feature-flag-moms.json에 키 등록
 * 2. PR merge 후 약 1분 25초 뒤 자동 반영 (재배포 불필요)
 * 3. 아래 훅으로 조회
 *
 * 조회 실패 시 기본값(false / '' / 빈 Set) 반환 — BE fail-safe 동작
 * staleTime FEATURE_FLAG_STALE_TIME(30s) — BE 캐시 갱신 주기와 맞춤
 */

const FEATURE_FLAG = 'FEATURE_FLAG'

export const featureFlagQueryKeys = {
  all: [FEATURE_FLAG] as const,
  flag: (key: string) => [...featureFlagQueryKeys.all, key] as const,
  boolean: (key: string, objectKey: string) => [...featureFlagQueryKeys.all, 'boolean', key, objectKey] as const,
  string: (key: string, objectKey: string) => [...featureFlagQueryKeys.all, 'string', key, objectKey] as const,
  values: (key: string) => [...featureFlagQueryKeys.all, 'values', key] as const,
  keys: (key: string) => [...featureFlagQueryKeys.all, 'keys', key] as const,
}

// 기능 on/off 제어
// json: { "feature.myFlag": { "default": false } }
export const useFeatureFlag = (key: string): UseQueryResult<boolean, Error> => {
  return useQuery({
    queryKey: featureFlagQueryKeys.flag(key),
    queryFn: () => getFeatureFlag(key),
    staleTime: FEATURE_FLAG_STALE_TIME,
    select: (data) => data ?? false,
  })
}

// object 타입 flag의 boolean 필드 조회
// json: { "my.flag": { "default": { "enabled": false, "otherField": "..." } } }
export const useFeatureFlagBoolean = (key: string, objectKey: string): UseQueryResult<boolean, Error> => {
  return useQuery({
    queryKey: featureFlagQueryKeys.boolean(key, objectKey),
    queryFn: () => getFeatureFlagBoolean(key, objectKey),
    staleTime: FEATURE_FLAG_STALE_TIME,
    select: (data) => data ?? false,
  })
}

// object 타입 flag의 string 필드 조회
// json: { "my.flag": { "default": { "activeShippers": "29CM,MUSINSA" } } }
export const useFeatureFlagString = (key: string, objectKey: string): UseQueryResult<string, Error> => {
  return useQuery({
    queryKey: featureFlagQueryKeys.string(key, objectKey),
    queryFn: () => getFeatureFlagString(key, objectKey),
    staleTime: FEATURE_FLAG_STALE_TIME,
    select: (data) => data ?? '',
  })
}

// 화이트리스트 관리 — 쉼표 구분 목록을 Set으로 반환
// json: { "my.flag": { "default": { "values": "A,B,C" } } }
// 활용: allowedSet.has(userEmail), allowedSet.has(centerCode)
export const useFeatureFlagValues = (key: string): UseQueryResult<Set<string>, Error> => {
  return useQuery({
    queryKey: featureFlagQueryKeys.values(key),
    queryFn: () => getFeatureFlagValues(key),
    staleTime: FEATURE_FLAG_STALE_TIME,
    select: (data) => new Set(data ?? []),
  })
}

// object 타입 flag 중 true 인 필드명을 Set으로 반환
// json: { "my.flag": { "default": { "fieldA": true, "fieldB": false } } } → Set{'fieldA'}
// 화면 밖(일반 함수)에서 같은 값을 읽을 땐 v2CommonApi.ts 처럼 queryClient.fetchQuery 로 같은 queryKey 를 쓴다.
export const useFeatureFlagKeys = (key: string): UseQueryResult<Set<string>, Error> => {
  return useQuery({
    queryKey: featureFlagQueryKeys.keys(key),
    queryFn: () => getFeatureFlagKeys(key),
    staleTime: FEATURE_FLAG_STALE_TIME,
    select: (data) => new Set(data ?? []),
  })
}
